import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';
import { DateFormat, SchedulingModalityEnum, StorageKeys } from '@core/enums';
import { ICancelSchedule, IDriver, IOpenSchedule, IPayloadDriver, IResponseModel, IConfigSchedule, IQuotationVO, IUserSchedule, IVehicleSchedule, ISchedulePayload, IAdvisor, IPaymentMethod } from '@core/interface';
import dayjs from 'dayjs';
import { TranslateService } from '@ngx-translate/core';
import { formatHourDefault, getMomentTimestamp } from '@core/utils';
import { API, environment } from '@core/envs';
import { MockService } from '../mock-service/mock.service';

@Injectable({
  providedIn: 'root'
})
export class ScheduleCoreService {
  readonly #http = inject(HttpClient);
  readonly #mockService = inject(MockService);
  readonly #translate = inject(TranslateService);
  readonly #country = sessionStorage.getItem(StorageKeys.COUNTRY);

  #modality = "DEALER";

  readonly #defaultPaymentMethod: IPaymentMethod = {
    online: false,
    gateway: 'Dealer',
    paymentId: null as unknown as number,
    status: 'dealer' as IPaymentMethod['status'],
    price: null as unknown as number,
    text: 'payment-methods.status.dealer',
    isRefund: null,
    paymentDate: null,
  };

  #normalizePaymentMethod(paymentMethod: unknown): IPaymentMethod[] {
    if (paymentMethod === null || paymentMethod === undefined) {
      return [{ ...this.#defaultPaymentMethod }];
    }

    if (Array.isArray(paymentMethod)) {
      return paymentMethod.length > 0 ? paymentMethod : [{ ...this.#defaultPaymentMethod }];
    }
    
    return [paymentMethod as IPaymentMethod];
  }

  readonly configScheduleSignal = signal<IConfigSchedule>({
    scheduleDate: '',
    scheduleHour: 0,
    advisor: 0,
    country: this.#country as string,
    scheduleModality: SchedulingModalityEnum.GENERAL_REPAIR,
    scheduleSource: sessionStorage.getItem(StorageKeys.SOURCE)?.substring(0, 1).toUpperCase() as string,
    scheduleType: 'R',
    division: 1,
    createS: getMomentTimestamp(new Date()),
    sendEmail: true,
    dealer: 0,
  });
  readonly userSchedule = signal<IUserSchedule>({
    customerRemark: '',
    customerId: '',
    contactCustomerId: '',
    createUser: 'WEB-',
    scheduleUser: '',
  });
  readonly vehicleSchedule = signal<IVehicleSchedule>({
    vin: '',
    vehiclePlate: '',
    model: '',
    group: undefined,
  });
  readonly quotations = signal<IQuotationVO[]>([]);
  readonly offeredServices = signal<number[]>([]);
  readonly advisor = signal<IAdvisor>({
    id: 0,
    name: '',
    timesToWork: [],
  });


  getOpened(vin: string): Observable<IResponseModel<IOpenSchedule[]>> {
    const url = `${environment.getRoute(API.scheduling)}/schedules/v2/list`;
    return this.#http.post<IResponseModel<IOpenSchedule[]>>(url, { vin, country: this.#country, status: [1] }).pipe(
      map(data => {
        data.response = data.response.map((schedule) => ({
          ...schedule,
          dateAndHour: `${this.formattedDate(schedule.scheduleDate)} - ${formatHourDefault(schedule.scheduleHour)}`,
          modality: this.#determineModality(schedule),
          paymentMethod: this.#normalizePaymentMethod(schedule.paymentMethod),
        }));
        return data;
      }),
      tap(data => sessionStorage.setItem(StorageKeys.OPEN_SCHEDULES, JSON.stringify(data)))
    );
  }

  delete(
    schedule: IOpenSchedule,
    cancelUser: string,
    reschedule: boolean
  ): Observable<IResponseModel<null>> {
    const url = `${environment.getRoute(API.transformation)}/schedules`;

    const body: ICancelSchedule = {
      cancelUser,
      reschedule,
      division: schedule.divisionId,
      country: schedule.country,
      dealer: schedule.dealerId,
      advisor: schedule.advisorId,
      scheduleHour: schedule.scheduleHour,
      scheduleDate: dayjs(schedule.scheduleDate).format(DateFormat.ISO),
    };

    return this.#http.delete<IResponseModel<null>>(url, { body }).pipe(
      tap((data) => console.log(data))
    );
  }

  getScheduleStatusData(vin: string, country: string, status: number): Observable<IResponseModel<Partial<IOpenSchedule[]>>> {
    const schedules = JSON.parse(sessionStorage.getItem(StorageKeys.STATUS_SCHEDULES) as string) as IResponseModel<Partial<IOpenSchedule[]>>;

    if (schedules.response?.length && vin === schedules?.response[0]?.vin) {
      return new Observable(data => data.next(schedules));
    }

    const url = `${environment.getRoute(API.scheduling)}/schedules/v2/status`;

    return this.#http.post<IResponseModel<Partial<IOpenSchedule[]>>>(url, { vin, country, status: [status] }).pipe(
      tap(data => sessionStorage.setItem(StorageKeys.OPEN_SCHEDULES, JSON.stringify(data)))
    );
  }

  getDriverSchedule(payload: IPayloadDriver): Observable<IResponseModel<IDriver>> {
    const url = `${environment.getRoute(API.scheduling)}/schedules/v2/drivers`;
    const scheduleDate = payload.date + 'T' + '00:00:00.000';

    const body = {
      vin: payload.vin,
      advisor: payload.advisor,
      country: payload.country,
      date: scheduleDate,
      dealer: payload.dealer,
      hour: payload.hour,
    };

    return this.#http.post<IResponseModel<IDriver>>(url, body).pipe(
      tap(data => {
        data.response = {
          ...data.response,
          dateAndHour: `${this.formattedDate(data.response.checkoutDate)} - ${formatHourDefault(data.response.checkoutHour)}`,
        };
      })
    );
  }

  getImageDriverSchedule(fileName: string): Observable<string> {
    let url: string = environment.getRoute(API.dealermedia);
    url = url.concat('/images/get/advisor/v2');

    return this.#http.post(url, { fileName }, { responseType: 'arraybuffer' }).pipe(
      map((response: ArrayBuffer) => {
        let binary = '';
        const bytes = new Uint8Array(response);
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        return 'data:image/png;base64,' + btoa(binary);
      })
    );
  }

  save() {

    const schedule: ISchedulePayload = {
      ...this.configScheduleSignal(),
      ...this.userSchedule(),
      ...this.vehicleSchedule(),
      offeredServices: this.offeredServices(),
      quotationScheduleVO: {
        quotationVOs: this.quotations(),
      },
    };
    console.log('schedule', schedule);
    // return;
    const api = environment.getRoute(API.transformation);
    const url = `${api}/schedules`;
    return this.#http.post<IResponseModel<null>>(url, schedule);
  }

  // Função auxiliar para determinar a modalidade do agendamento
  
  setModality(modality: string) {
    if (modality !== this.#modality) {
      this.#resetSelectedServices();
    }
    this.#modality = modality;
  }

  getModality() {
    return this.#modality;
  }

  formattedDate(date: string) {
    const utcDate = new Date(date);
    const weekday = utcDate.getUTCDay().toString();
    const formattedDate = `${utcDate.getUTCDate().toString().padStart(2, '0')}/${(utcDate.getUTCMonth() + 1).toString().padStart(2, '0')}/${utcDate.getUTCFullYear()}`;

    return `${this.#translate.instant('weekdays.' + weekday)}, ${formattedDate}`;
  }

  // Função auxiliar para determinar a modalidade
  #determineModality(schedule: IOpenSchedule): SchedulingModalityEnum {
    if (schedule?.pickupDelivery) {
      return SchedulingModalityEnum.PDL;
    } else if (schedule?.winchService) {
      return SchedulingModalityEnum.WINCH;
    } else if (schedule?.mobileService) {
      return SchedulingModalityEnum.MSV;
    } else {
      return SchedulingModalityEnum.DEALER;
    }
  }

  #resetSelectedServices() {
    this.#mockService.additionalServices.set(null);
  }

  /*
  * Método para resetar as configurações de agendamento
  */
  resetSessionConfigSchedule() {
    localStorage.setItem('@Ford-selected-date', '');
    localStorage.setItem('@Ford-selected-hour', '');
    localStorage.setItem('@Ford-selected-advisor', '');
    localStorage.setItem('@Ford-selected-advisor-name', '');
  }

  /**
   * Método para verificar se a modalidade de agendamento é de experiência remota
   * @returns boolean
   */
  isRemoteExperienceModality(): boolean {
    const modality = this.getModality();
    return modality === SchedulingModalityEnum.PDL || modality === SchedulingModalityEnum.MSV;
  }
}
